import { useTranslation } from "react-i18next";
import { ClipboardCheck, Clock, Lock, MapPin, Radio } from "lucide-react";
import { formatSessionTime } from "@/utils/datetime";

type S = { id: string; group_name?: string | null; room_name?: string | null; start_at: string; end_at: string; status: string; attendance_marked?: boolean };

export function pickHeroSession(items: S[], now = Date.now()): { s: S; kind: "now" | "pending" | "next" } | null {
    const live = items.filter((s) => s.status !== "cancelled");
    const at = (x: string) => new Date(x).getTime();
    const cur = live.find((s) => at(s.start_at) <= now && now < at(s.end_at));
    if (cur) return { s: cur, kind: "now" };
    const pending = [...live].reverse().find((s) => at(s.end_at) <= now && !s.attendance_marked);
    if (pending) return { s: pending, kind: "pending" };
    const next = live.find((s) => at(s.start_at) > now);
    return next ? { s: next, kind: "next" } : null;
}

export function NextLessonHero({ items, tz, onOpen }: { items: S[]; tz: string; onOpen: (id: string) => void }) {
    const { t } = useTranslation();
    const pick = pickHeroSession(items || []);
    if (!pick) return null;
    const { s, kind } = pick;
    return (
        <div className="surface-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dashboard-next-lesson" data-kind={kind}
            style={{ borderLeft: "4px solid var(--primary)" }}>
            <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: kind === "now" ? "var(--success-fg)" : "var(--accent)" }}>
                    {kind === "now" ? <Radio size={13} className="animate-pulse" /> : <Clock size={13} />}
                    {t("fast_att.hero.title")} · {t(`fast_att.hero.${kind}`)}
                </div>
                <div className="text-xl font-semibold truncate" style={{ color: "var(--text)" }} data-testid="dashboard-next-lesson-group">{s.group_name || "—"}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm" style={{ color: "var(--text-muted)" }}>
                    <span className="font-mono">{formatSessionTime(s.start_at, tz)}–{formatSessionTime(s.end_at, tz)}</span>
                    {s.room_name && <span className="inline-flex items-center gap-1"><MapPin size={13} />{s.room_name}</span>}
                    {s.attendance_marked && <span className="inline-flex items-center gap-1" style={{ color: "var(--success-fg)" }}><Lock size={13} />{t("attendance.marked_locked")}</span>}
                </div>
            </div>
            <button data-testid="dashboard-quick-attendance-btn" onClick={() => onOpen(s.id)}
                className="min-h-[52px] w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl px-6 text-base font-semibold shadow-sm active:scale-[0.98] transition-transform"
                style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                <ClipboardCheck size={18} />{t("fast_att.quick_btn")}
            </button>
        </div>
    );
}
